import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import isPlainObject from 'lodash/isPlainObject';
import React from 'react';
import { FunctionField, TextField, useResourceDefinitions } from 'react-admin';
import { dateAndTime } from '../../../utils/date';
import { ResourceLink } from '../../reusable/ResourceLink';
import InfiniteList from '../components/InfiniteList';
import { WriteOnlyDatagrid } from '../components/WriteOnlyDatagrid';
import { AuditLogFilters } from './AuditLogFilters';
import {
  type AuditLogRow,
  collectChangedFieldsFromAuditUpdate,
  formatAuditLogFieldKeyAsLabel,
  formatAuditLogJson,
  formatAuditLogValueForDisplay,
  getAdminResourceNameForAuditAction,
  getAuditLogFieldLabelForCreateEntry,
  getAuditLogPrincipalSummary,
  getOrderedFieldKeysForAuditCreateEntry,
  getResourceLinkTargetForAuditValue,
  IMPORT_APML_CATEGORIES,
} from './auditLogDisplay';

const renderJsonBlock = (json: string) => (
  <Box
    component="pre"
    sx={{
      fontFamily: 'monospace',
      fontSize: '0.75rem',
      margin: 0,
      overflowWrap: 'anywhere',
      whiteSpace: 'pre-wrap',
    }}
  >
    {json}
  </Box>
);

const inlineKvSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  columnGap: 0.75,
} as const;

const auditColumnSx = { minWidth: 320 } as const;

/** Caption above a stacked audit value (ID, Name, change rows). */
function StackedAuditField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function renderAuditLogFieldValue(
  value: unknown,
  fieldKey: string,
  primaryEntityResource: string | undefined,
  registeredResources: ReadonlySet<string>,
): React.ReactNode {
  const linkTarget = getResourceLinkTargetForAuditValue(
    value,
    fieldKey,
    primaryEntityResource,
    registeredResources,
  );
  if (linkTarget) {
    return (
      <ResourceLink name={linkTarget.resource} id={linkTarget.id} type="show">
        {linkTarget.id}
      </ResourceLink>
    );
  }
  return (
    <Typography component="span" variant="body2">
      {formatAuditLogValueForDisplay(value)}
    </Typography>
  );
}

const AuditChangesDisplay: React.FC<{
  action: string;
  data?: Record<string, unknown>;
}> = ({ action, data }) => {
  const resourceDefinitions = useResourceDefinitions();
  const registeredResources = React.useMemo(
    () => new Set(Object.keys(resourceDefinitions)),
    [resourceDefinitions],
  );

  if (
    action === IMPORT_APML_CATEGORIES ||
    data === undefined ||
    !isPlainObject(data)
  ) {
    return (
      <Box sx={auditColumnSx}>{renderJsonBlock(formatAuditLogJson(data))}</Box>
    );
  }

  if (action.startsWith('create_')) {
    const primaryEntityResource = getAdminResourceNameForAuditAction(action);
    if (primaryEntityResource && typeof data.id === 'string' && data.id) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getOrderedFieldKeysForAuditCreateEntry(data).map((key) => (
              <TableRow>
                <TableCell>
                  <Box
                    component="span"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    {getAuditLogFieldLabelForCreateEntry(key)}:
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography
                    key={key}
                    variant="body2"
                    component="div"
                    sx={inlineKvSx}
                  >
                    {renderAuditLogFieldValue(
                      data[key],
                      key,
                      primaryEntityResource,
                      registeredResources,
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
  }

  if (action.startsWith('update_')) {
    const primaryEntityResource = getAdminResourceNameForAuditAction(action);
    const previousData = data.previousData;
    const updatedData = data.updatedData;
    if (
      primaryEntityResource &&
      isPlainObject(previousData) &&
      isPlainObject(updatedData)
    ) {
      const previousRecord = previousData as Record<string, unknown>;
      const updatedRecord = updatedData as Record<string, unknown>;
      const idRaw = previousRecord.id ?? data.id;
      if (typeof idRaw === 'string' && idRaw) {
        const nameRaw = previousRecord.name;
        const changes = collectChangedFieldsFromAuditUpdate(
          previousRecord,
          updatedRecord,
        );
        return (
          <>
            <Box display="flex" gap={1}>
              <StackedAuditField label="ID">
                {renderAuditLogFieldValue(
                  idRaw,
                  'id',
                  primaryEntityResource,
                  registeredResources,
                )}
              </StackedAuditField>
              {Boolean(nameRaw) && (
                <StackedAuditField label="Name">
                  {renderAuditLogFieldValue(
                    nameRaw,
                    'name',
                    primaryEntityResource,
                    registeredResources,
                  )}
                </StackedAuditField>
              )}
            </Box>
            {changes.length > 0 ? (
              <>
                <Typography
                  component="div"
                  variant="body2"
                  fontWeight={600}
                  sx={{ mt: 0.5 }}
                >
                  Changes
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Old</TableCell>
                      <TableCell>New</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {changes.map(({ key, from, to }) => (
                      <TableRow>
                        <TableCell>
                          {formatAuditLogFieldKeyAsLabel(key)}
                        </TableCell>
                        <TableCell>
                          {renderAuditLogFieldValue(
                            from,
                            key,
                            primaryEntityResource,
                            registeredResources,
                          )}
                        </TableCell>
                        <TableCell>
                          {renderAuditLogFieldValue(
                            to,
                            key,
                            primaryEntityResource,
                            registeredResources,
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : null}
          </>
        );
      }
    }
  }

  if (data.previousData !== undefined || data.updatedData !== undefined) {
    return (
      <Box display="grid" gap={1} sx={auditColumnSx}>
        <Box>
          <Typography component="div" variant="body2" fontWeight={600}>
            Old
          </Typography>
          {renderJsonBlock(formatAuditLogJson(data.previousData))}
        </Box>
        <Box>
          <Typography component="div" variant="body2" fontWeight={600}>
            New
          </Typography>
          {renderJsonBlock(formatAuditLogJson(data.updatedData))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={auditColumnSx}>{renderJsonBlock(formatAuditLogJson(data))}</Box>
  );
};

const AuditChangesField: React.FC<{ record?: AuditLogRow }> = ({ record }) => {
  if (!record) {
    return <Typography variant="body2">—</Typography>;
  }

  return (
    <AuditChangesDisplay
      action={record.action}
      data={
        record.data == null
          ? undefined
          : (record.data as Record<string, unknown>)
      }
    />
  );
};

const AuditUserField: React.FC<{ record?: AuditLogRow }> = ({ record }) => {
  if (!record) {
    return <Typography variant="body2">—</Typography>;
  }

  const principal = getAuditLogPrincipalSummary(record.principal);

  if (principal.kind === 'text') {
    return <Typography variant="body2">{principal.label}</Typography>;
  }

  return (
    <Box>
      <ResourceLink name="User" id={principal.id} type="show">
        <Typography component="div" variant="body2">
          {principal.name}
        </Typography>
        <Typography component="div" variant="caption" fontSize={10}>
          {principal.id.split('-')[0]}
        </Typography>
      </ResourceLink>
    </Box>
  );
};

export const AuditLogList: React.FC = () => (
  <Box marginTop={2}>
    <InfiniteList filters={<AuditLogFilters />}>
      <WriteOnlyDatagrid
        bulkActionButtons={false}
        rowClick={false}
        sx={{
          '& .column-createdAt': { width: '10%' },
          '& .column-user': { width: '15%' },
          '& .column-action': { width: '10%' },
          '& .column-changes': { width: '65%' },
        }}
      >
        <FunctionField<AuditLogRow>
          source="createdAt"
          label="Timestamp"
          sortable={false}
          render={(record) => (record ? dateAndTime(record.createdAt) : '—')}
        />
        <FunctionField<AuditLogRow>
          source="user"
          label="User"
          sortable={false}
          render={(record) => <AuditUserField record={record} />}
        />
        <TextField source="action" label="Action" sortable={false} />
        <FunctionField<AuditLogRow>
          source="changes"
          label="Changes"
          sortable={false}
          render={(record) => <AuditChangesField record={record} />}
        />
      </WriteOnlyDatagrid>
    </InfiniteList>
  </Box>
);
